/**
 * Cross-window coordination contract for the dual-window document viewer
 * (OR-22558). Adapted from the `dsc-dual-window-poc` `src/coord/messages.ts`
 * pattern: the opener (Review window) broadcasts which document is active and
 * the viewer window renders it.
 *
 * Semantics are **load-and-persist**, not continuous mirroring: the viewer shows
 * the selected document and keeps showing it until a newer selection arrives or
 * the session stops being active. There is deliberately no heartbeat or
 * staleness-clear (a backgrounded/closed opener must not blank a document the
 * analyst is reading), and no blur-on-`pagehide` (a normal opener refresh must
 * not blank the viewer).
 *
 * Only identifiers cross the wire — never the presigned URL. The viewer
 * resolves its own short-lived download URL from `appId`/`docId`.
 */

/** BroadcastChannel name. Same-origin only (both windows share the app origin). */
export const CHANNEL_NAME = "dsc-doc-viewer";

/**
 * Session signal the opener sends so the viewer can clear itself when the
 * analyst's session is no longer usable. `loggedOut` and `expired` are both
 * derived from an unauthenticated session today (the app can't distinguish
 * them); `lockLost` means a held VLA lock was taken by another user.
 */
export type SessionState = "active" | "loggedOut" | "expired" | "lockLost";

/** Stamped onto every message by `sendCoord`. */
export interface MessageMeta {
  /** Per-page-load id of the sending window; used to ignore our own messages. */
  tabId: string;
  /** Epoch ms when sent; drives last-writer-wins ordering. */
  sentAt: number;
  /** Unique id per message. */
  msgId: string;
}

/** The active document plus session signal (identifiers only — no URL). */
export interface DocumentPayload {
  appId: string;
  docId: string;
  fileName: string;
  contentType: string;
  sessionState: SessionState;
  reviewedBy?: string | null;
}

/** Opener → viewer: this document is now active (on click / change / handshake reply). */
export interface DocumentFocusedMessage extends DocumentPayload {
  type: "doc-focused";
  meta: MessageMeta;
}

/** Viewer → opener: sent on mount so the opener replies with the current document. */
export interface RequestDocumentMessage {
  type: "request-doc";
  meta: MessageMeta;
}

/** Opener → viewer: a deal's opener tab gained focus. Used to clear the viewer when a different deal's tab takes over. */
export interface AppContextChangedMessage {
  type: "app-context-changed";
  appId: string;
  meta: MessageMeta;
}

/** Opener → viewer: user session ended (logout or expiry). Clears viewer state. */
export interface SessionEndedMessage {
  type: "session-ended";
  reason: "logout" | "session-expired";
  meta: MessageMeta;
}

/** Viewer → opener: a document moved to a different approval. Refresh the docs list. */
export interface DocumentMovedMessage {
  type: "doc-moved";
  appId: string;
  docId: string;
  meta: MessageMeta;
}

export type CoordMessage =
  | DocumentFocusedMessage
  | RequestDocumentMessage
  | AppContextChangedMessage
  | SessionEndedMessage
  | DocumentMovedMessage;

/**
 * Narrows an arbitrary BroadcastChannel payload to a coordination message.
 * @param {unknown} value - The raw `MessageEvent.data`.
 * @returns {boolean} True when the value is one of our coordination messages.
 */
export function isCoordMessage(value: unknown): value is CoordMessage {
  return typeof value === "object" && value !== null && "type" in value && "meta" in value;
}
